import React from 'react';
import { PassengerCard } from '@/components/molecules';
import { useWizard } from '@/modules/wizard';
import PassengersForm from '@/forms/passengers-form';
import { FlightPassengersProps } from './FlightPassengers.type';
import { InnerWrapper } from './FlightPassengers.styles';
import { useTranslation } from 'next-i18next';
import { GroupedPassengers } from '@/components/molecules/PriceResumeCard/PriceResumeCard.types';
import { extractTimeFromDatetime, formatDate } from '@/utils/helpers';
import { Button, Icon } from '@/components/atoms';
import UrlMapper from '@/config/url-mapper';
import { useTheme } from 'styled-components';

const FlightPassengers: React.FC<FlightPassengersProps> = ({ passengers }) => {
    const { t } = useTranslation(); // Translations
    const { state, dispatch } = useWizard();
    const theme = useTheme();

    const handleEdit = (position: number) => {
        dispatch({
            type: 'UPDATE_DATA',
            payload: {
                passengerActive: position,
                allowReserve: false,
            },
        });
    };

    const getStatus = (index: number) => {
        if (state.data?.allowReserve) {
            return 'saved';
        }
        return index === state.data?.passengerActive ? 'active' : state.data?.travelers && state.data?.travelers[index] !== undefined ? 'saved' : 'disabled';
    };

    const groupedPassengers = passengers?.reduce((groups: GroupedPassengers, passenger) => {
        const { type } = passenger;

        if (!groups[type]) {
            groups[type] = {
                type,
                total: 0,
            };
        }

        groups[type].total += 1;

        return groups;
    }, {});

    const passengersString =
        Object.values(groupedPassengers || [])
            .map((group) => t(`flights:passenger.${group.type}${group.total > 1 ? '_plural' : ''}`, { count: group.total }))
            .join(` ${t('flights:payment.and')} `) + '.';

    const outboundRouteSelectedString = `${t('flights:departure-flight.section.title')}: ${formatDate(state?.data?.outboundRouteSelected?.departingDatetime as string)} >> ${state
        ?.data?.outboundRouteSelected?.origin.code} · ${extractTimeFromDatetime(state?.data?.outboundRouteSelected?.departingDatetime as string)} - ${state?.data
        ?.outboundRouteSelected?.destination.code} · ${extractTimeFromDatetime(state?.data?.outboundRouteSelected?.arrivingDatetime as string)}`;

    const returnRouteSelectedString = state?.data?.returnRouteSelected
        ? ` | ${t('flights:return-flight.section.title')}: ${formatDate(state?.data?.returnRouteSelected?.departingDatetime as string)} >> ${state?.data?.returnRouteSelected
              ?.origin.code} · ${extractTimeFromDatetime(state?.data?.returnRouteSelected?.departingDatetime as string)} - ${state?.data?.returnRouteSelected?.destination
              .code} · ${extractTimeFromDatetime(state?.data?.returnRouteSelected?.arrivingDatetime as string)}`
        : '';

    const whatsappMessage = `${t('flights:payment.button.contact-message')} ${passengersString} ${outboundRouteSelectedString}${returnRouteSelectedString}`.trim();

    return (
        <InnerWrapper>
            {React.Children.toArray(
                passengers?.map((passenger, index) => (
                    <PassengerCard
                        key={`${passenger.type}-${passenger.number}`}
                        passenger={passenger}
                        initialData={state.data?.travelers && state.data?.travelers[index]}
                        onEdit={() => handleEdit(index)}
                        status={getStatus(index)}
                    >
                        <PassengersForm
                            passenger={passenger}
                            initialData={state.data?.travelers && state.data?.travelers[state.data?.passengerActive as number]}
                            position={index}
                            isTheFirst={index === 0}
                            isTheLast={passengers.length === index + 1}
                        />
                    </PassengerCard>
                ))
            )}
            <Button
                as="a"
                style={{ marginTop: 50 }}
                icon={<Icon.Agent size={20} color={theme.colors.primary.primary10} strokeWidth={2} />}
                iconPosition="left"
                content={t('flights:payment.button.contact-agent')}
                type="button"
                variant="filled"
                role="button"
                aria-label={t('flights:payment.button.contact-agent')}
                target="_blank"
                href={`${UrlMapper.whatsapp_agents}/?text=${whatsappMessage}`}
                rel="noopener noreferrer"
            />
        </InnerWrapper>
    );
};

export default FlightPassengers;
